import React__default from 'react';

/**
 * Hook for managing state related to showing a confirmation modal.
 *
 * @template Context - The type of the context provided to the confirmation.
 *
 * @returns state The confirmation state
 * @returns state.pending Whether the confirmation is pending (i.e., if the modal should be shown).
 * @returns state.context The provided context to the confirmation (whatever was last provided to "confirm").
 * @returns state.proceed Function to give confirmation (typically the affirmative action shown in the modal).
 * @returns state.cancel Function to cancel confirmation (typically the various cancellation flows in the modal).
 * @returns state.confirm Function to call when the confirmation flow should start, returns a promise that resolves after a call to cancel or proceed.
 */
function useConfirmation() {
    const [pending, setPending] = React__default.useState(false);
    const [context, setContext] = React__default.useState(undefined);
    const [proceed, setProceed] = React__default.useState(undefined);
    const [cancel, setCancel] = React__default.useState(undefined);
    const confirm = React__default.useCallback((context) => {
        const promise = new Promise((resolve, reject) => {
            setPending(true);
            setContext(context);
            setProceed(() => () => resolve());
            setCancel(() => () => reject());
        });
        return promise.then(() => {
            setPending(false);
            return true;
        }, () => {
            setPending(false);
            return false;
        });
    }, []);
    return React__default.useMemo(() => ({
        pending,
        context,
        proceed,
        cancel,
        confirm,
    }), [pending, context, proceed, cancel, confirm]);
}

export { useConfirmation };
